import { useState, useMemo } from 'react';
import clsx from 'clsx';
import { MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import ZoneCard from './ZoneCard';

export default function ZoneSelector({ zones = [], onSelectZone, isLoading = false }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  // Filter zones based on search term
  const filteredZones = useMemo(() => {
    if (!searchTerm.trim()) return zones;

    const term = searchTerm.toLowerCase();
    return zones.filter(zone =>
      zone.name?.toLowerCase().includes(term) ||
      zone.code?.toLowerCase().includes(term) ||
      zone.type?.toLowerCase().includes(term)
    );
  }, [zones, searchTerm]);

  const handleSelectZone = (zone) => {
    setSelectedId(zone.id);
    onSelectZone(zone);
  };

  return (
    <div className="p-6 space-y-4 h-full flex flex-col">
      {/* Search Input */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search zones by name, code, or type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input pl-10 w-full"
        />
      </div>

      {/* Zones Count */}
      <div className="text-sm text-slate-600">
        {filteredZones.length === 0 ? (
          zones.length === 0 ? (
            <p className="text-slate-500">No zones available yet</p>
          ) : (
            <p>No zones match "{searchTerm}"</p>
          )
        ) : (
          <p>
            Showing {filteredZones.length} of {zones.length} zone{zones.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Zone List */}
      <div className="flex-1 overflow-y-auto space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <p className="text-sm text-slate-500">Loading zones...</p>
            </div>
          </div>
        ) : filteredZones.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
            <p>
              {zones.length === 0
                ? '👉 No zones available for selection'
                : '🔍 No zones match your search'}
            </p>
          </div>
        ) : (
          filteredZones.map(zone => (
            <ZoneCard
              key={zone.id}
              zone={zone}
              isSelected={selectedId === zone.id}
              onSelect={() => handleSelectZone(zone)}
            />
          ))
        )}
      </div>

      {/* Info Message */}
      {filteredZones.length > 0 && (
        <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded border border-slate-200">
          💡 Click a zone card to select it. The zone will be assigned to your camera.
        </div>
      )}
    </div>
  );
}
